import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Wand2 } from "lucide-react";

type Props = {
  companyId: string;
  /** Day to build, ISO (yyyy-mm-dd). Defaults to tomorrow. */
  date?: string;
  label?: string;
  size?: "sm" | "default";
  variant?: "default" | "outline" | "secondary";
  onDone?: () => void;
};

const tomorrowISO = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
};

/**
 * Runs the same nightly scheduling logic on demand so a brand-new company sees
 * a real, optimized route without waiting for the 2am job.
 */
export default function GenerateScheduleButton({
  companyId,
  date,
  label = "Generate my schedule",
  size = "sm",
  variant = "default",
  onDone,
}: Props) {
  const [running, setRunning] = useState(false);

  const run = async () => {
    setRunning(true);
    const target = date ?? tomorrowISO();
    const { data, error } = await supabase.functions.invoke("daily-schedule", {
      body: { company_id: companyId, date: target },
    });
    setRunning(false);
    const payload = data as { error?: string; companies?: { created?: number }[] } | null;
    if (error || payload?.error) {
      toast.error(payload?.error ?? "Could not build the schedule");
      return;
    }
    const created = payload?.companies?.[0]?.created ?? 0;
    toast.success(
      created > 0
        ? `${created} stop${created === 1 ? "" : "s"} scheduled and routed for ${target}`
        : `Schedule for ${target} is up to date`
    );
    onDone?.();
  };

  return (
    <Button size={size} variant={variant} disabled={running} onClick={run}>
      <Wand2 className="mr-2 h-4 w-4" />
      {running ? "Building…" : label}
    </Button>
  );
}
